import { FullStory } from '@fullstory/browser';
import { useEffect, useRef, useState } from 'react';
import { DashboardHeader } from './DashboardHeader';
import { TextboxModal } from '@/app/shared/components/TextboxModal/TextboxModal';
import { InvoicesTable } from './InvoicesTable';
import { useOktaAuth } from '@okta/okta-react';
import { useInvoicesTable } from '@/app/shared/hooks/useInvoicesTable';
import { useInvoicesSummary } from '@/app/shared/hooks/useInvoicesSummary';
import { useDisputedReasons } from '@/app/shared/hooks/useDisputedReasons';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { useDebtorAccount } from '@/app/shared/utils/hooks/useDebtorAccount';
import { useCompany } from '@/app/shared/utils/hooks/useCompany';
import { useFeatureFlags } from '@/app/shared/utils/hooks/useFeatureFlags';
import {
  IconNames,
  ToastMessages,
  MessagePosition,
  MessageType,
} from '@triumph/design-system';
import { ToastMessageProps } from '@triumph/design-system/components/ToastMessages/ToastMessage/ToastMessage.types';
import { useTranslation } from 'react-i18next';
import { InvoicesSummaryCard } from '../Widgets/InvoicesSummaryCard';
import {
  DashboardContentContainer,
  InvoicesTableContainer,
  Sidebar,
} from './Dashboard.StyledComponents';
import { PaymentInstructionDetail } from '../Widgets/PaymentInstructions';
import { useModal } from '@/app/shared/hooks/useModal';
import { UpdatePaymentStatusModal } from '@/app/shared/components/UpdatePaymentStatusModal/UpdatePaymentStatusModal';
import { useNavigate } from 'react-router-dom';
import { PageRoutes } from '@/app/components/router/routes';
import LoadingPage from '@/app/shared/utils/LoadingPage';
import { CreditUtilizationWidget } from '../Widgets/CreditUtilization';
import { NoaCenterWidget } from '../Widgets/NoaCenterWidget';

export const DebtorPortalDashboard: React.FC = () => {
  const { theme } = useAppContext();
  const { t } = useTranslation('invoicesDashboard');
  const navigate = useNavigate();
  const { authState } = useOktaAuth();

  // Use debtor context instead of managing auth inline
  const {
    selectedDebtor,
    isInternal,
    initialized: isDebtorInitialized,
    isDebtorSwitching,
  } = useDebtorAccount();

  const { selectedCompanyId, selectedCompany, companies } = useCompany();
  const { EnableCompanySelector, EnableNoaCenter } = useFeatureFlags();

  // When the company selector is enabled, the payment info follows the selected
  // company. When disabled, preserve the original behavior of always showing the
  // default company (Triumph, id 1).
  const paymentInfoCompany = EnableCompanySelector
    ? selectedCompany
    : companies.find((c) => c.companyId === 1) ?? null;

  const isAuthConfigured = isDebtorInitialized && !!selectedDebtor;

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const prevDebtorIdRef = useRef<number | null>(null);

  const [modalInputValue, setModalInputValue] = useState('');
  const [modalTextValue, setModalTextValue] = useState('');
  const [isModalVisible, toggleModal] = useState(false);
  const [showDisputedReasons, toggleShowDisputedReasons] = useState(false);
  const [modalDisputedReasonValue, setModalDisputedReasonValue] = useState('');

  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  const {
    isOpen: isPayDateModalOpen,
    openModal: openPayDateModal,
    closeModal: closePayDateModal,
  } = useModal();

  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<MessageType>(MessageType.success);
  const [showToast, setShowToast] = useState(false);

  const { getDisplayValueById } = useDisputedReasons({
    enabled: isAuthConfigured,
  });

  const {
    rows,
    totalRows,
    availableFilters,
    isLoading: isTableLoading,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handleOnSort,
    searchTerm,
    handleSearchChange,
    selectedDateRangeIndex,
    currentDateRange,
    handleDateRangeChange,
    handlePaymentsNotReceivedChange,
    handleUpcomingInvoicesChange,
    handlePaymentsPastDueChange,
    needsAttention,
    handleNeedsAttentionChange,
    buttonFilters,
    handleButtonFilterChange,
    selectedInvoices,
    handleSelectInvoice,
    handleSelectAllOnPage,
    handleClearAllSelections,
    allCurrentPageSelected,
    someCurrentPageSelected,
    handlePaymentDateChange,
    handleBulkPaymentDateChangeAndSubmit,
    handleBulkWillNotPayChangeAndSubmit,
    updateRowWithModalData,
    getRowByInvoiceNumber,
    handleExportList,
    isExporting,
    submitError,
    resetAllFilters,
    hasActiveFilters,
    filterResetKey,
  } = useInvoicesTable({ enabled: isAuthConfigured });

  const { invoiceSummary, isLoading: isSummaryLoading } = useInvoicesSummary({
    enabled: isAuthConfigured,
  });

  // Reset hasLoadedOnce when debtor changes so loading page shows for new debtor
  useEffect(() => {
    const currentDebtorId = selectedDebtor?.debtorId ?? null;
    if (prevDebtorIdRef.current !== null && prevDebtorIdRef.current !== currentDebtorId) {
      setHasLoadedOnce(false);
    }
    prevDebtorIdRef.current = currentDebtorId;
  }, [selectedDebtor?.debtorId]);

  useEffect(() => {
    if (isAuthConfigured && !isTableLoading && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [isAuthConfigured, isTableLoading, hasLoadedOnce]);

  const resetToast = () => {
    setShowToast(false);
    setToastMessage('');
  };

  const displayToast = (message: string, type: MessageType) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const messages: ToastMessageProps[] = [
    {
      handleClose: resetToast,
      type: toastType,
      message: toastMessage,
      show: showToast,
      messageId: 1,
      toastTestId: 'invoice_submit_toast',
      autoCloseTime: 3000,
    },
  ];

  // FullStory identity
  useEffect(() => {
    if (authState?.isAuthenticated && selectedDebtor) {
      const oktaUid = authState?.idToken?.claims?.sub;
      if (oktaUid) {
        FullStory('setIdentity', {
          uid: oktaUid,
          properties: {
            debtorId: selectedDebtor.debtorId,
            isInternal,
          },
        });
      }
    }
  }, [authState?.isAuthenticated, selectedDebtor, isInternal]);

  // Single invoice modal handlers
  const handleNotesChange = (
    invoiceNumber: string,
    includeDisputedReasons: boolean,
  ) => {
    const row = getRowByInvoiceNumber(invoiceNumber);
    if (includeDisputedReasons) {
      toggleShowDisputedReasons(true);
    }
    setModalDisputedReasonValue(row?.disputedReasonId ?? '');
    setModalInputValue(invoiceNumber);
    setModalTextValue(row?.userText ?? '');
    toggleModal(true);
  };

  const handleModalSubmit = (
    invoiceNumber: string,
    value: string,
    disputedReasonId?: string,
    disputedReasonDesc?: string,
  ) => {
    updateRowWithModalData(
      invoiceNumber,
      value,
      disputedReasonId,
      disputedReasonDesc,
    );
    handleModalClose();
  };

  const handleModalClose = () => {
    setModalInputValue('');
    setModalTextValue('');
    toggleShowDisputedReasons(false);
    toggleModal(false);
  };

  const handleUpdatePaymentStatus = () => {
    if (selectedInvoices.size === 0) {
      displayToast(
        t('toastMessages.selectInvoiceWarning'),
        MessageType.warning,
      );
      return;
    }
    openPayDateModal();
  };

  const handleBulkInvoiceDetails = () => {
    if (selectedInvoices.size === 0 || selectedInvoices === undefined) {
      displayToast(
        t('toastMessages.selectInvoiceWarning'),
        MessageType.warning,
      );
      return;
    }

    const selectedInvoicesArray = Array.from(selectedInvoices);

    const invoiceMap = selectedInvoicesArray
      .map((invoiceNumber) => {
        const row = rows.find((r) => r.invoiceNumber === invoiceNumber);
        return {
          invoiceNumber: invoiceNumber,
          invoiceId: row?.invoiceId,
        };
      })
      .filter((item) => item.invoiceId !== undefined);

    if (invoiceMap.length === 0) {
      displayToast(
        t('toastMessages.selectInvoiceWarning'),
        MessageType.warning,
      );
      return;
    }

    const firstInvoiceId = invoiceMap[0].invoiceId;

    navigate(
      PageRoutes.INVOICES.replace(':invoiceId', String(firstInvoiceId)),
      {
        state: {
          selectedInvoices: selectedInvoicesArray,
          invoiceMap: invoiceMap,
        },
      },
    );
  };

  const handleBulkPayDateSubmit = async (
    invoiceNumbers: string[],
    selectedPaymentDate: string,
    note: string = '',
  ): Promise<void> => {
    const count = invoiceNumbers.length;
    setIsBulkSubmitting(true);

    try {
      const success = await handleBulkPaymentDateChangeAndSubmit(
        invoiceNumbers,
        selectedPaymentDate,
        note,
      );

      if (success) {
        handleClearAllSelections();
        displayToast(
          t('toastMessages.paymentDateSuccess', { count }),
          MessageType.success,
        );
      } else {
        displayToast(
          t('toastMessages.updateError', { error: submitError }),
          MessageType.error,
        );
        throw new Error('Update failed');
      }
    } catch (error) {
      displayToast(t('toastMessages.updateRetryError'), MessageType.error);
      throw error;
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const handleBulkWillNotPaySubmit = async (
    invoiceNumbers: string[],
    disputedReasonId: string,
    disputedReasonDesc: string,
    note: string,
  ): Promise<void> => {
    const count = invoiceNumbers.length;
    setIsBulkSubmitting(true);

    try {
      const success = await handleBulkWillNotPayChangeAndSubmit(
        invoiceNumbers,
        disputedReasonId,
        disputedReasonDesc,
        note,
      );

      if (success) {
        handleClearAllSelections();
        displayToast(
          t('toastMessages.willNotPaySuccess', { count }),
          MessageType.success,
        );
      } else {
        displayToast(
          t('toastMessages.updateError', { error: submitError }),
          MessageType.error,
        );
        throw new Error('Update failed');
      }
    } catch (error) {
      displayToast(t('toastMessages.updateRetryError'), MessageType.error);
      throw error;
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  if (isDebtorSwitching) {
    return <LoadingPage />;
  }

  // Internal user with no debtor selected
  if (isDebtorInitialized && isInternal && !selectedDebtor) {
    return (
      <div>
        <ToastMessages
          messages={messages}
          position={MessagePosition.topCenter}
        />
        <DashboardHeader />
      </div>
    );
  }

  const isInitialized = isAuthConfigured && hasLoadedOnce;

  return (
    <div>
      <ToastMessages messages={messages} position={MessagePosition.topCenter} />
      {isInitialized ? (
        <div>
          <DashboardHeader />
          <DashboardContentContainer>
            <InvoicesTableContainer>
              <InvoicesTable
                rows={rows}
                totalInvoices={totalRows}
                availableFilters={availableFilters ?? {}}
                currentPage={currentPage}
                pageSize={pageSize}
                isLoading={isTableLoading}
                handleNotesChange={handleNotesChange}
                handlePaymentDateChange={handlePaymentDateChange}
                handleOnSort={handleOnSort}
                handleDateRangeChange={handleDateRangeChange}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
                selectedDateRangeIndex={selectedDateRangeIndex}
                currentDateRange={currentDateRange}
                needsAttention={needsAttention}
                handleNeedsAttentionChange={handleNeedsAttentionChange}
                selectedInvoices={selectedInvoices}
                onSelectInvoice={handleSelectInvoice}
                onSelectAllOnPage={handleSelectAllOnPage}
                allCurrentPageSelected={allCurrentPageSelected}
                someCurrentPageSelected={someCurrentPageSelected}
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                buttonFilters={buttonFilters}
                onButtonFilterChange={handleButtonFilterChange}
                onExportList={handleExportList}
                isExporting={isExporting}
                onUpdatePaymentStatus={handleUpdatePaymentStatus}
                onBulkInvoiceDetails={handleBulkInvoiceDetails}
                getDisplayValueById={getDisplayValueById}
                onResetFilters={resetAllFilters}
                hasActiveFilters={hasActiveFilters}
                filterResetKey={filterResetKey}
              />
            </InvoicesTableContainer>
            <Sidebar data-testid="sidebar">
              <div data-testid="invoices-summary-payments-past-due">
                <InvoicesSummaryCard
                  totalAmount={
                    invoiceSummary?.paymentsPastDue?.totalAmount || '0'
                  }
                  count={invoiceSummary?.paymentsPastDue?.count || '0'}
                  headerText={t('invoicesSummary.paymentsPastDue')}
                  iconColor={theme.colors.semantic.dangerDefault}
                  iconName={IconNames.Alerts_WarningFilled}
                  onChangeFilter={handlePaymentsPastDueChange}
                  isLoading={isSummaryLoading}
                />
              </div>
              <div data-testid="invoices-summary-payments-not-received">
                <InvoicesSummaryCard
                  totalAmount={
                    invoiceSummary?.paymentsNotReceived?.totalAmount || '0'
                  }
                  count={invoiceSummary?.paymentsNotReceived?.count || '0'}
                  headerText={t('invoicesSummary.paymentsNotReceived')}
                  iconColor={theme.colors.semantic.dangerDefault}
                  iconName={IconNames.Alerts_Alert}
                  onChangeFilter={() => handlePaymentsNotReceivedChange(true)}
                  isLoading={isSummaryLoading}
                />
              </div>
              <div data-testid="invoices-summary-upcoming">
                <InvoicesSummaryCard
                  totalAmount={
                    invoiceSummary?.upcomingInvoices?.totalAmount || '0'
                  }
                  count={invoiceSummary?.upcomingInvoices?.count || '0'}
                  headerText={t('invoicesSummary.upcomingInvoices')}
                  iconColor={theme.colors.greyscale.gray50}
                  iconName={IconNames.Currency_Savings}
                  onChangeFilter={handleUpcomingInvoicesChange}
                  isLoading={isSummaryLoading}
                />
              </div>
              {EnableNoaCenter && (
                <div data-testid="noa-center-widget-container">
                  <NoaCenterWidget />
                </div>
              )}
              {selectedDebtor && selectedCompanyId && (
                <div data-testid="credit-utilization-widget-container">
                  <CreditUtilizationWidget
                    debtorId={selectedDebtor.debtorId}
                    companyId={selectedCompanyId}
                  />
                </div>
              )}
              <div data-testid="payment-instruction-details">
                <PaymentInstructionDetail company={paymentInfoCompany} />
              </div>
            </Sidebar>
          </DashboardContentContainer>
          <TextboxModal
            isVisible={isModalVisible}
            showDisputedReasons={showDisputedReasons}
            rows={rows}
            inputValue={modalInputValue}
            textValue={modalTextValue}
            reasonValue={modalDisputedReasonValue}
            handleClose={handleModalClose}
            handleSubmit={handleModalSubmit}
            setTextValue={setModalTextValue}
            setReasonValue={setModalDisputedReasonValue}
          />
          <UpdatePaymentStatusModal
            isVisible={isPayDateModalOpen}
            handleClose={closePayDateModal}
            selectedInvoices={selectedInvoices}
            onBulkPaymentDateChange={handleBulkPayDateSubmit}
            onBulkWillNotPayChange={handleBulkWillNotPaySubmit}
            isSubmitting={isBulkSubmitting}
          />
        </div>
      ) : (
        <LoadingPage />
      )}
    </div>
  );
};